import {
  BooleanFilterOption,
  DataFilterOption,
} from '@asset-metrix/portal-gateway-client-js';
import { applyFormatters, EMPTY_VALUE } from '@asset-metrix/portal-ui';
import { t } from 'i18next';

import { FilterConfigDto } from '../../store/states/filterTypes';
import { formatDetailPageLinkDisplayValue } from '../../utils/detailPageLinkDisplay';
import { formattersByType } from '../table/configToColDef';

export type FilterFormatter = (
  value: string | boolean | null | undefined
) => string | boolean | null | undefined;

export const getFilterFormatters = (pageFilterConfig: FilterConfigDto) => {
  const formatterType = pageFilterConfig.type;
  const formatters = formattersByType[formatterType] ?? [];
  return <TValue>(value: TValue) => {
    return applyFormatters(formatters, value, {
      trueLabel: t('app.label.true'),
      falseLabel: t('app.label.false'),
      ...pageFilterConfig.formatOptions,
    });
  };
};

const parseFilterUrlValueForFormatting = (
  value: string,
  filterConfig: FilterConfigDto
): string | number => {
  if (filterConfig.type !== 'number') {
    return value;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? value : parsed;
};

export function formatPageFilterUrlValuesForDisplay(
  values: string[],
  filterConfig: FilterConfigDto | undefined
): string {
  if (values.length === 0) {
    return '';
  }

  if (filterConfig == null) {
    return formatDetailPageLinkDisplayValue(values) ?? '';
  }

  const formatter = getFilterFormatters(filterConfig);
  const parts = values
    .filter(Boolean)
    .map((value) =>
      String(
        formatter(parseFilterUrlValueForFormatting(value, filterConfig)) ??
          value
      )
    );

  return parts.length > 0 ? parts.join(', ') : '';
}

const normalizeFilterOptionLabel = (
  value: string | boolean | null | undefined
): string => {
  if (value == null) {
    return EMPTY_VALUE;
  }

  const normalized = String(value);
  return normalized === '' ? EMPTY_VALUE : normalized;
};

export const resolveFilterOptionLabel = (
  option: string | DataFilterOption | BooleanFilterOption,
  value: string | boolean,
  formatter: FilterFormatter
): string => {
  const optionLabel = typeof option === 'string' ? undefined : option.label;

  if (optionLabel == null || optionLabel === '') {
    return normalizeFilterOptionLabel(formatter(value) ?? value);
  }

  return normalizeFilterOptionLabel(optionLabel);
};

export const mapFilterOptionToSelectData = (
  option: string | DataFilterOption | BooleanFilterOption,
  formatter: FilterFormatter
): { label: string; value: string | boolean } | null => {
  const value = typeof option === 'string' ? option : option.value;
  if (value == null) {
    return null;
  }

  return {
    label: resolveFilterOptionLabel(option, value, formatter),
    value,
  };
};
